import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import type { Category, Product } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a successful save so the settings page can refresh its summary. */
  onSaved: () => void
}

/**
 * Picks which menu items earn loyalty stamps.
 *
 * The critical thing to understand: an EMPTY selection means "every item
 * earns", not "nothing earns". That's the default state and the only reason
 * tenants who never open this dialog keep the historic behaviour. Ticking even
 * one item switches the whole programme into allow-list mode, so the dialog
 * says so out loud before you save — a coffee shop that ticks its coffees has
 * just stopped its desserts from earning, which is the point, but a tenant who
 * ticks one thing by accident would otherwise silently kill earning shop-wide.
 *
 * Structure mirrors TagManagerDialog's item picker (searchable, category-
 * filterable checkbox list, select-all-shown header) so the two feel the same.
 */
export function LoyaltyEligibleItemsDialog({ open, onOpenChange, onSaved }: Props) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('all')
  const [prefillLoading, setPrefillLoading] = useState(false)

  const productsQuery = useQuery({
    queryKey: ['loyalty-eligible-picker-products'],
    queryFn: () => apiClient.getAdminProducts({ per_page: 1000 }).then((r) => r.data ?? []),
    enabled: open,
    staleTime: 60_000,
  })
  const products = (productsQuery.data as Product[] | undefined) ?? []

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.getCategories().then((r) => (r.data as Category[]) ?? []),
    enabled: open,
    staleTime: 60_000,
  })
  const categories = (categoriesQuery.data as Category[] | undefined) ?? []
  const categoryName = useMemo(() => {
    const m = new Map<string, string>()
    categories.forEach((c) => m.set(c.id, c.name))
    return m
  }, [categories])

  // Seed the checkboxes from the saved set each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setSearch('')
    setCategoryId('all')
    setPrefillLoading(true)
    apiClient
      .getLoyaltyEligibleProducts()
      .then((r) => setSelected(new Set(r.data ?? [])))
      .catch((err) => toastHelpers.apiError('Load eligible items', err))
      .finally(() => setPrefillLoading(false))
  }, [open])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return products.filter((p) => {
      if (categoryId !== 'all' && p.category_id !== categoryId) return false
      if (q && !p.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [products, search, categoryId])

  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id))

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Select-all respects the active search + category filter, which is what
  // makes "tick every coffee" one click: filter to Coffee, then tick the header.
  const toggleAllFiltered = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) filtered.forEach((p) => next.delete(p.id))
      else filtered.forEach((p) => next.add(p.id))
      return next
    })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.setLoyaltyEligibleProducts([...selected])
      if (!res.success) throw new Error(res.message || 'Failed to save eligible items')
      return selected.size
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['loyalty-eligible-products'] })
      onSaved()
      onOpenChange(false)
      toastHelpers.success(
        'Eligible items saved',
        count === 0
          ? 'Every item on the menu now earns stamps.'
          : `${count} item${count === 1 ? '' : 's'} earn stamps. Everything else earns nothing.`,
      )
    },
    onError: (err: unknown) => toastHelpers.apiError('Save eligible items', err),
  })

  const busy = saveMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Items that earn stamps</DialogTitle>
          <DialogDescription>
            Tick the items a customer has to buy to earn loyalty. Useful when you only want to
            reward what you make yourself — e.g. your own coffee, but not bought-in desserts.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {/* The empty-vs-non-empty distinction decides whether the whole menu
              earns, so state the consequence of the CURRENT selection rather
              than leaving the operator to infer it after saving. */}
          <div className="rounded-md border border-border/70 bg-muted/40 px-3 py-2 text-sm">
            {selected.size === 0 ? (
              <span>
                <strong>Nothing ticked — every item earns.</strong> This is the default. Tick items
                only if you want to limit earning to some of your menu.
              </span>
            ) : (
              <span>
                <strong>
                  {selected.size} item{selected.size === 1 ? '' : 's'} will earn stamps.
                </strong>{' '}
                Every other item earns nothing, and a bill with none of these earns nothing at all.
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search items…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border">
            <div className="flex items-center gap-3 border-b bg-muted/40 px-3 py-2">
              <Checkbox
                checked={allFilteredSelected}
                onCheckedChange={toggleAllFiltered}
                disabled={filtered.length === 0}
                aria-label="Select all shown"
              />
              <span className="text-xs text-muted-foreground">
                Select all shown ({filtered.length})
              </span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {productsQuery.isLoading || prefillLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Loading items…</div>
              ) : filtered.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No items match your filters.
                </div>
              ) : (
                <ul className="divide-y">
                  {filtered.map((p) => (
                    <li key={p.id}>
                      <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted/30">
                        <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                        <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {p.category_id ? categoryName.get(p.category_id) ?? '—' : '—'}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={busy || prefillLoading}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
